import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import * as z from "zod";

import { EntityOverviewListDto } from "api/generated/model";
import { accessRightOptionalAdminSchema } from "components/form/rhfFields/rhfAccessRights";
import { useEntitiesQuery } from "org/entities/queries/useEntitiesQuery";
import {
    ControlEntityAccessRightsInstance,
    RiskEntityAccessRightsInstance,
} from "utils/accessRights/models";

const MAX_TITLE = 255;
const MAX_DESCRIPTION = 100000;

export const getNameSchema = (t: TFunction) =>
    z
        .string()
        .trim()
        .min(1, { error: t("Validation_Required") })
        .max(MAX_TITLE);

export const descriptionSchema = z
    .string()
    .max(MAX_DESCRIPTION)
    .transform((str) => trimAndDecode(str));

export function trimAndDecode(str: string): string {
    // Replace common HTML space entities with real spaces, then trim
    return str.replace(/&#x20;|&nbsp;|&#32;/g, " ").trim();
}

export function useControlEntityViewFormSchema() {
    const { t } = useTranslation();
    // NOTE: this is a temporary bad pattern, but we need it so that users who are not entity admins,
    // but have edit rights to the specific entity can edit the name and validate its uniqueness
    const { data: entities, status } = useEntitiesQuery({
        all: true,
        scope: "control",
    });

    return z.object({
        name: getNameSchema(t).refine(
            (name: string) => checkNameAvailability(name, entities, status),
            {
                message: t("Entities_Edit_Error_EntityAlreadyExists"),
            }
        ),
        description: descriptionSchema,
        responsibleId: z.number(),
        controlUserIdAndRights: accessRightOptionalAdminSchema(
            ControlEntityAccessRightsInstance
        ),
        riskUserIdAndRights: accessRightOptionalAdminSchema(
            RiskEntityAccessRightsInstance
        ),
        kind: z.literal("Control"),
        tagIds: z.array(z.guid()),
    });
}

export type ControlEntityFormSchema = z.infer<
    ReturnType<typeof useControlEntityViewFormSchema>
>;

// We need to check the name availability only against ControlEntities -> scope param in the query (not all Entities including RiskEntities)
function checkNameAvailability(
    name: string,
    entities: EntityOverviewListDto,
    status: "success" | "error"
): boolean {
    switch (status) {
        case "success":
            const normalizedInput = normalizeName(name);
            return !entities.content.some(
                (entity) =>
                    normalizeName(entity.resource.name) === normalizedInput
            );
        case "error":
            return true;
        default:
            return assertNever(status);
    }
}

function normalizeName(name: string): string {
    return name.trim().toLowerCase();
}
